import React from 'react'

import {
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    CircularProgress,
} from '@material-ui/core'
import {
    Mail as MailIcon,
    Visibility as EyeIcon,
    TouchApp as MousePointerIcon,
    Timeline as ActivityIcon,
    Flag as TargetIcon,
} from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

const useStyles = makeStyles((theme: Theme) => ({
    activityCard: {
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    activityItem: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(248, 250, 252, 0.5)',
        },
    },
    activityIcon: {
        padding: theme.spacing(1),
        borderRadius: theme.spacing(1.5),
        marginRight: theme.spacing(2),
    },
    tabContent: {
        width: '100%',
        paddingLeft: `calc(${theme.spacing(3)} + 24px)`,
        paddingRight: `calc(${theme.spacing(3)} + 24px)`,
    },
}))

interface ActivityItem {
    type: 'conversion' | 'click' | 'open' | 'sent'
    prospect: string
    action: string
    time: string
}

interface RecentActivityTabProps {
    recentActivity: ActivityItem[]
    isLoading?: boolean
    error?: Error | null | unknown
}

export const RecentActivityTab: React.FC<RecentActivityTabProps> = ({
    recentActivity,
    isLoading = false,
    error = null,
}) => {
    const classes = useStyles()

    if (isLoading) {
        return (
            <Box className={classes.tabContent}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{ minHeight: '400px' }}
                >
                    <CircularProgress />
                </Box>
            </Box>
        )
    }

    if (error && error instanceof Error) {
        return (
            <Box className={classes.tabContent}>
                <Card className={classes.activityCard}>
                    <CardContent>
                        <Typography variant="body1" color="error">
                            Failed to load activity
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    return (
        <Box className={classes.tabContent}>
            <Card className={classes.activityCard}>
                <CardHeader
                    title={
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 8 }}
                        >
                            <ActivityIcon />
                            <Typography variant="h6">
                                Recent Activity
                            </Typography>
                        </Box>
                    }
                    subheader="Real-time campaign activity and prospect engagement"
                />
                <CardContent>
                    {recentActivity.length === 0 ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            style={{ padding: 48 }}
                        >
                            <ActivityIcon
                                style={{
                                    fontSize: 48,
                                    color: '#94a3b8',
                                    marginBottom: 16,
                                }}
                            />
                            <Typography variant="h6" gutterBottom>
                                No activity yet
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Activity will appear here as prospects engage
                                with your campaign
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                            }}
                        >
                            {recentActivity.map((activity, index) => (
                                <Box
                                    key={index}
                                    className={classes.activityItem}
                                >
                                    <Box
                                        className={classes.activityIcon}
                                        style={{
                                            backgroundColor:
                                                activity.type === 'conversion'
                                                    ? 'rgba(16, 185, 129, 0.1)'
                                                    : activity.type === 'click'
                                                    ? 'rgba(139, 92, 246, 0.1)'
                                                    : activity.type === 'open'
                                                    ? 'rgba(59, 130, 246, 0.1)'
                                                    : 'rgba(248, 250, 252, 1)',
                                        }}
                                    >
                                        {activity.type === 'conversion' ? (
                                            <TargetIcon
                                                style={{
                                                    color: '#10b981',
                                                    fontSize: 16,
                                                }}
                                            />
                                        ) : activity.type === 'click' ? (
                                            <MousePointerIcon
                                                style={{
                                                    color: '#8b5cf6',
                                                    fontSize: 16,
                                                }}
                                            />
                                        ) : activity.type === 'open' ? (
                                            <EyeIcon
                                                style={{
                                                    color: '#3b82f6',
                                                    fontSize: 16,
                                                }}
                                            />
                                        ) : (
                                            <MailIcon
                                                style={{
                                                    color: '#94a3b8',
                                                    fontSize: 16,
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box flex={1}>
                                        <Typography
                                            variant="body1"
                                            style={{ fontWeight: 500 }}
                                        >
                                            {activity.prospect}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            {activity.action}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        {activity.time}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    )
}
